"use client";

import { useContext, useEffect, forwardRef, useRef } from "react";
import { AppContext } from "@/context/AppContext";
import { usePathname } from "next/navigation";
import { stripLangPrefix } from "@/i18n/config";
import MobileFooter from "@/components/MobileFooter";

export const Content = forwardRef<HTMLElement, { children: React.ReactNode }>(
  function Content({ children, ...props }, ref) {
    const { setActivePage, activePage } = useContext(AppContext);
    const pathname = usePathname();
    const prevPathRef = useRef<string | null>(null);

    useEffect(() => {
      // Wenn wir auf der gleichen Seite bleiben (z.B. bei Klick auf ein bereits aktives Menüelement),
      // verhindern wir unnötige Neusetzungen
      if (prevPathRef.current === pathname) {
        return;
      }

      // Verwende die gleiche Logik wie im AppContext
      // (Sprachpräfix /en bzw. internes /de zuerst entfernen)
      const { path } = stripLangPrefix(pathname);
      if (path === '/') {
        setActivePage('home');
      } else if (path.startsWith('/work')) {
        setActivePage('work');
      } else if (path.startsWith('/blog')) {
        setActivePage('blog');
      } else if (path.startsWith('/about')) {
        setActivePage('about');
      } else if (path.startsWith('/contact')) {
        setActivePage('contact');
      } else if (path.startsWith('/imprint')) {
        setActivePage('imprint');
      } else if (path.startsWith('/privacy')) {
        setActivePage('privacy');
      } else {
        setActivePage('home'); // Fallback
      }

      // Aktuellen Pfad speichern
      prevPathRef.current = pathname;
    }, [pathname, setActivePage]);

    // WICHTIG: Kein key={pathname} im Container mit der Background-Transition verwenden,
    // da dies ein vollständiges Remounting verursachen würde und die Transition unterbrechen

    return (
      <main ref={ref} {...props}>
        {/* Der content--page bleibt bestehen, selbst bei Seitenwechseln */}
        <div className={`content--page ${activePage}-page`}>
          {/* Der Background-Container muss stabil bleiben (kein Remounting) */}
          <div className="fixed inset-0 z-[-1] overflow-hidden" style={{ opacity: 1 }}>
            <div className="content--page__background"></div>
          </div>
          <div className="content--wrapper">
            {children}
            <MobileFooter />
          </div>
        </div>
      </main>
    );
  }
);